import { ShoppingCart, Star, TrendingUp, ExternalLink, Package } from 'lucide-react'

export default function AmazonProductsPanel({ amazonData }) {
  if (!amazonData) return null

  // Flatten products from all countries
  const allProducts = []
  Object.entries(amazonData).forEach(([country, products]) => {
    if (Array.isArray(products)) {
      products.forEach(p => allProducts.push({ ...p, country }))
    }
  })

  if (allProducts.length === 0) return null

  const countries = Object.keys(amazonData)

  const renderStars = (rating) => {
    if (!rating) return null
    const full = Math.floor(rating)
    return (
      <span className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-3 h-3 ${i < full ? 'text-slate-lighter fill-slate-lighter' : 'text-slate-medium'}`}
          />
        ))}
        <span className="text-xs text-slate-lighter ml-1">{rating}</span>
      </span>
    )
  }

  return (
    <div className="gradient-glow p-6 rounded-xl card-hover">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <ShoppingCart className="w-5 h-5 text-slate-lighter" />
        <h3 className="text-lg font-bold text-white">Amazon — Productos Encontrados</h3>
        <div className="flex gap-1.5 ml-auto">
          {countries.map(c => (
            <span key={c} className="text-xs px-2 py-0.5 bg-slate-darker border border-slate-dark/40 text-slate-lighter rounded">
              {c}
            </span>
          ))}
        </div>
      </div>

      {/* Products grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {allProducts.slice(0, 9).map((product, idx) => (
          <div
            key={idx}
            className="p-3 bg-slate-darker border border-slate-dark/30 rounded-lg hover:border-slate-dark/60 transition-all flex flex-col gap-2"
          >
            {/* Country badge + title */}
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs px-1.5 py-0.5 bg-slate-dark text-slate-lighter rounded shrink-0">
                {product.country}
              </span>
              {product.url && product.url !== '#' ? (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-light hover:text-slate-lighter transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : null}
            </div>

            <p className="text-sm text-slate-lightest font-medium line-clamp-2 leading-snug">
              {product.title || 'Producto sin título'}
            </p>

            {/* Price + Rating */}
            <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-dark/30">
              <div>
                {product.price ? (
                  <span className="text-base font-bold text-slate-lighter">
                    {product.currency === 'USD' ? '$' : product.currency}{product.price}
                  </span>
                ) : (
                  <span className="text-xs text-slate-light">Precio N/D</span>
                )}
              </div>
              <div className="text-right">
                {renderStars(product.rating)}
                {product.review_count && (
                  <p className="text-xs text-slate-light mt-0.5">
                    {product.review_count.toLocaleString()} reseñas
                  </p>
                )}
              </div>
            </div>

            {/* BSR */}
            {product.bsr && (
              <div className="flex items-center gap-1 text-xs text-slate-light">
                <TrendingUp className="w-3 h-3" />
                <span>BSR #{product.bsr.toLocaleString()}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary footer */}
      <div className="mt-4 p-3 bg-slate-darker rounded-lg border border-slate-dark/20">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-slate-light" />
          <p className="text-xs text-slate-300">
            <span className="font-semibold text-slate-lighter">{allProducts.length}</span> productos encontrados en Amazon {countries.join(', ')}.
            {' '}Analiza precios, ratings y BSR para identificar oportunidades de posicionamiento.
          </p>
        </div>
      </div>
    </div>
  )
}
